import React, { useCallback, useEffect, useMemo, useState } from "react";
import { clearSession, isAuthed } from "../../auth";
import {
  downloadArtifact,
  getConsoleArtifacts,
  getConsoleCommands,
  getConsoleHistory,
  getConsoleSession,
  runConsoleCommand,
} from "../../services/consoleApi";

/**
 * Operations console: a web front-end over the allowlisted `flask ...` commands
 * in app/cli.py. Deliberately styled as a standalone terminal so it is never
 * mistaken for the tenant-facing application.
 */

function defaultArgs(command) {
  const out = {};
  for (const p of command.params) {
    out[p.name] = p.default === undefined ? (p.kind === "flag" ? false : "") : p.default;
  }
  return out;
}

/** Mirror of the server-side argv builder, for the live command preview. */
function previewCommandLine(command, args) {
  const parts = ["flask", ...command.argv];
  for (const p of command.params) {
    const value = args[p.name];
    if (p.kind === "flag") {
      if (value) parts.push(p.opt);
      continue;
    }
    if (value === null || value === undefined || String(value).trim() === "") continue;
    parts.push(p.opt, String(value));
  }
  return parts.join(" ");
}

function Field({ param, value, onChange }) {
  const label = (
    <div className="flex items-baseline gap-2">
      <span className="font-mono text-[13px] text-emerald-300">{param.opt}</span>
      {param.required ? <span className="text-[11px] text-rose-400">required</span> : null}
    </div>
  );

  if (param.kind === "flag") {
    return (
      <label className="flex items-start gap-3 py-2 cursor-pointer">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-1 h-4 w-4 accent-emerald-400"
        />
        <span>
          {label}
          {param.help ? <div className="text-xs text-slate-400 mt-0.5">{param.help}</div> : null}
        </span>
      </label>
    );
  }

  return (
    <div className="py-2">
      {label}
      {param.kind === "choice" ? (
        <select
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1 w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 font-mono text-sm text-slate-100 focus:outline-none focus:border-emerald-500"
        >
          {param.choices.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={param.kind === "int" ? "number" : "text"}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={param.default != null ? String(param.default) : ""}
          className="mt-1 w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 font-mono text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:border-emerald-500"
        />
      )}
      {param.help ? <div className="text-xs text-slate-400 mt-1">{param.help}</div> : null}
    </div>
  );
}

export default function OpsConsole() {
  const [phase, setPhase] = useState("checking"); // checking | denied | ready | error
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  const [commands, setCommands] = useState([]);

  const [selected, setSelected] = useState(null);
  const [args, setArgs] = useState({});
  const [confirmed, setConfirmed] = useState(false);

  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [runError, setRunError] = useState(null);

  const [tab, setTab] = useState("output"); // output | history | artifacts
  const [history, setHistory] = useState([]);
  const [artifacts, setArtifacts] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const s = await getConsoleSession();
        const c = await getConsoleCommands();
        if (!alive) return;
        setSession(s);
        setCommands(c.commands || []);
        setPhase("ready");
      } catch (e) {
        if (!alive) return;
        // The API answers 403 for anyone who is not a platform admin.
        if (/403|forbidden/i.test(e?.message || "")) setPhase("denied");
        else {
          setError(e?.message || "Console unavailable");
          setPhase("error");
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const selectCommand = useCallback((command) => {
    setSelected(command);
    setArgs(defaultArgs(command));
    setConfirmed(false);
    setResult(null);
    setRunError(null);
    setTab("output");
  }, []);

  const groups = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const matching = commands.filter(
      (c) =>
        !q ||
        c.name.toLowerCase().includes(q) ||
        (c.summary || "").toLowerCase().includes(q)
    );
    const map = new Map();
    for (const c of matching) {
      if (!map.has(c.group)) map.set(c.group, []);
      map.get(c.group).push(c);
    }
    return [...map.entries()];
  }, [commands, filter]);

  const commandLine = selected ? previewCommandLine(selected, args) : "";
  const needsConfirm = !!selected?.confirm;

  async function onRun() {
    if (!selected || running) return;
    setRunning(true);
    setRunError(null);
    setResult(null);
    setTab("output");
    try {
      const res = await runConsoleCommand({
        command: selected.name,
        args,
        confirm: confirmed,
      });
      setResult(res);
      setConfirmed(false);
      if (res?.ok) {
        // Fresh output may have produced files; drop the cache.
        setArtifacts(null);
      }
    } catch (e) {
      setRunError(e?.message || "Run failed");
    } finally {
      setRunning(false);
    }
  }

  async function openHistory() {
    setTab("history");
    try {
      const h = await getConsoleHistory();
      setHistory(h.runs || []);
    } catch (e) {
      setRunError(e?.message || "Failed to load history");
    }
  }

  async function openArtifacts() {
    setTab("artifacts");
    try {
      const a = await getConsoleArtifacts();
      setArtifacts(a);
    } catch (e) {
      setRunError(e?.message || "Failed to list artifacts");
    }
  }

  if (phase === "checking") {
    return (
      <Shell>
        <div className="p-8 font-mono text-sm text-slate-400">Authenticating…</div>
      </Shell>
    );
  }

  if (phase === "denied" || !isAuthed()) {
    return (
      <Shell>
        <div className="p-8 font-mono text-sm">
          <div className="text-rose-400">403 — platform admin credentials required.</div>
          <button
            onClick={() => {
              clearSession();
              window.location.assign("/login");
            }}
            className="mt-4 text-emerald-400 underline"
          >
            Sign in with another account
          </button>
        </div>
      </Shell>
    );
  }

  if (phase === "error") {
    return (
      <Shell>
        <div className="p-8 font-mono text-sm text-rose-400">{error}</div>
      </Shell>
    );
  }

  return (
    <Shell>
      <header className="flex items-center justify-between border-b border-slate-800 px-4 py-2.5">
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-sm text-emerald-400">wkmngmt://ops-console</span>
          <span className="text-xs text-slate-500">{commands.length} commands</span>
        </div>
        <div className="flex items-center gap-4 text-xs text-slate-400">
          <span className="font-mono">{session?.user?.email}</span>
          <button
            onClick={() => {
              clearSession();
              window.location.assign("/login");
            }}
            className="hover:text-slate-200"
          >
            sign out
          </button>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Command list */}
        <nav className="w-72 shrink-0 border-r border-slate-800 flex flex-col min-h-0">
          <div className="p-3 border-b border-slate-800">
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="filter commands…"
              className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 font-mono text-xs text-slate-100 placeholder-slate-600 focus:outline-none focus:border-emerald-500"
            />
          </div>
          <div className="overflow-y-auto flex-1 py-2">
            {groups.map(([group, items]) => (
              <div key={group} className="mb-3">
                <div className="px-3 pb-1 text-[10px] uppercase tracking-widest text-slate-500">
                  {group}
                </div>
                {items.map((c) => (
                  <button
                    key={c.name}
                    onClick={() => selectCommand(c)}
                    className={`w-full text-left px-3 py-1.5 font-mono text-xs transition ${
                      selected?.name === c.name
                        ? "bg-emerald-500/10 text-emerald-300 border-l-2 border-emerald-400"
                        : "text-slate-300 hover:bg-slate-800/60 border-l-2 border-transparent"
                    }`}
                  >
                    {c.name}
                    {c.writes ? <span className="ml-1.5 text-amber-500">•</span> : null}
                  </button>
                ))}
              </div>
            ))}
            {groups.length === 0 ? (
              <div className="px-3 text-xs text-slate-500">no match</div>
            ) : null}
          </div>
          <div className="border-t border-slate-800 px-3 py-2 text-[10px] text-slate-500">
            <span className="text-amber-500">•</span> writes to the database
          </div>
        </nav>

        {/* Detail + output */}
        <main className="flex-1 flex flex-col min-w-0">
          {!selected ? (
            <div className="flex-1 grid place-items-center text-sm text-slate-500 font-mono">
              select a command
            </div>
          ) : (
            <>
              <div className="border-b border-slate-800 p-4 overflow-y-auto max-h-[52%]">
                <h1 className="font-mono text-base text-slate-100">{selected.name}</h1>
                <p className="text-sm text-slate-400 mt-1">{selected.summary}</p>

                {selected.params.length > 0 ? (
                  <div className="mt-4 grid gap-x-8 md:grid-cols-2">
                    {selected.params.map((p) => (
                      <Field
                        key={p.name}
                        param={p}
                        value={args[p.name]}
                        onChange={(v) => setArgs((prev) => ({ ...prev, [p.name]: v }))}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="mt-3 text-xs text-slate-500">No options.</div>
                )}

                <div className="mt-4 rounded border border-slate-700 bg-slate-950 px-3 py-2 font-mono text-xs text-emerald-300 overflow-x-auto whitespace-pre">
                  $ {commandLine}
                </div>

                <div className="mt-4 flex items-center gap-4 flex-wrap">
                  <button
                    onClick={onRun}
                    disabled={running || (needsConfirm && !confirmed)}
                    className="rounded bg-emerald-500 px-4 py-1.5 font-mono text-sm text-slate-950 font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-emerald-400"
                  >
                    {running ? "running…" : "run"}
                  </button>
                  {needsConfirm ? (
                    <label className="flex items-center gap-2 text-xs text-amber-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={confirmed}
                        onChange={(e) => setConfirmed(e.target.checked)}
                        className="h-4 w-4 accent-amber-400"
                      />
                      I understand this command changes live data
                    </label>
                  ) : null}
                </div>
              </div>

              <div className="flex items-center gap-1 border-b border-slate-800 px-3 text-xs">
                {[
                  ["output", "output", () => setTab("output")],
                  ["history", "history", openHistory],
                  ["artifacts", "artifacts", openArtifacts],
                ].map(([key, label, onClick]) => (
                  <button
                    key={key}
                    onClick={onClick}
                    className={`px-3 py-2 font-mono ${
                      tab === key
                        ? "text-emerald-300 border-b-2 border-emerald-400"
                        : "text-slate-400 hover:text-slate-200 border-b-2 border-transparent"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="flex-1 overflow-auto p-4 font-mono text-xs">
                {runError ? <div className="text-rose-400 mb-3">{runError}</div> : null}

                {tab === "output" ? (
                  running ? (
                    <div className="text-slate-500">executing…</div>
                  ) : result ? (
                    <>
                      <div
                        className={`mb-2 ${result.ok ? "text-emerald-400" : "text-rose-400"}`}
                      >
                        exit {result.exit_code} · {result.duration_ms}ms
                      </div>
                      <AzureBackupBanner output={result.output} />
                      <pre className="whitespace-pre-wrap text-slate-200">{result.output}</pre>
                      {result.error ? (
                        <pre className="whitespace-pre-wrap text-rose-400 mt-2">
                          {result.error}
                        </pre>
                      ) : null}
                    </>
                  ) : (
                    <div className="text-slate-500">no output yet</div>
                  )
                ) : null}

                {tab === "history" ? (
                  history.length === 0 ? (
                    <div className="text-slate-500">no runs recorded in this process</div>
                  ) : (
                    <table className="w-full text-left">
                      <tbody>
                        {history.map((h, i) => (
                          <tr key={i} className="border-b border-slate-800/60">
                            <td className="py-1.5 pr-3 text-slate-500 whitespace-nowrap">
                              {h.ran_at}
                            </td>
                            <td className="py-1.5 pr-3 text-slate-400">{h.actor_email}</td>
                            <td className="py-1.5 pr-3 text-slate-200">{h.command_line}</td>
                            <td
                              className={`py-1.5 whitespace-nowrap ${
                                h.failed ? "text-rose-400" : "text-emerald-400"
                              }`}
                            >
                              exit {h.exit_code}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                ) : null}

                {tab === "artifacts" ? (
                  !artifacts ? (
                    <div className="text-slate-500">loading…</div>
                  ) : (
                    <>
                      <div className="text-slate-500 mb-2">{artifacts.dir}</div>
                      {artifacts.files.length === 0 ? (
                        <div className="text-slate-500">empty</div>
                      ) : (
                        artifacts.files.map((f) => (
                          <div key={f.name} className="flex items-center gap-3 py-1">
                            <button
                              onClick={() => downloadArtifact(f.name)}
                              className="text-emerald-400 hover:underline"
                            >
                              {f.name}
                            </button>
                            <span className="text-slate-500">{f.size} B</span>
                            <span className="text-slate-600">{f.modified_at}</span>
                          </div>
                        ))
                      )}
                    </>
                  )
                ) : null}
              </div>
            </>
          )}
        </main>
      </div>
    </Shell>
  );
}

/**
 * Scans db-backup output for the "[azure] BACKUP_FOLDER: <folder>" sentinel line
 * and renders a highlighted banner with the folder path when found.
 */
function AzureBackupBanner({ output }) {
  if (!output) return null;
  const match = output.match(/\[azure\] BACKUP_FOLDER:\s*(.+)/);
  if (!match) return null;
  const folder = match[1].trim();
  return (
    <div className="mb-3 flex items-start gap-2 rounded border border-sky-700 bg-sky-950/60 px-3 py-2 text-xs">
      <span className="text-sky-400 shrink-0">☁ Azure backup</span>
      <span className="font-mono text-sky-200 break-all">{folder}</span>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="fixed inset-0 flex flex-col bg-slate-900 text-slate-200 antialiased">
      {children}
    </div>
  );
}
